import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";
import { useCallback, useEffect } from "react";
import { secondMs } from "verdi-common/timeConstants";

import { generateUID } from "verdi-common/utils/HelperFunctions";
import MapEntityBase from "../../../../services/map-management/map-entities/map-entity-base";

interface UseRefreshDeviceProps {
    onRefresh?: (newModel: MapEntityBase["model"]) => void;
    model: MapEntityBase["model"];
}

/**
 * Hook to trigger a callback when the device is refreshed
 */
export const useRefreshDevice = ({ model, onRefresh }: UseRefreshDeviceProps) => {
    const refreshDevice: (minAgeOfLastRefresh?: number) => Promise<void> = useCallback(
        async (minAgeOfLastRefresh = 1.5 * secondMs) => {
            if (Math.abs(Date.now() - (model.lastRefreshed || 0).valueOf()) > minAgeOfLastRefresh && model?.refresh) {
                try {
                    await model.refresh();
                } catch (e) {
                    console.warn("Failed to refresh device", e);
                }
            }
        },
        [model],
    );

    // Listen to and handle changes to the device
    useEffect(() => {
        const listenerID = generateUID("DeviceManualOverrideUpdateListener");

        // Attach listeners to the model and recent data updated event
        model.onChange.addListener(() => {
            onRefresh?.(model);
        }, listenerID);
        SessionHandler.onRecentDataUpdated.addListener((e: { affectedDevices: Set<string> }) => {
            if (e.affectedDevices.has(model.id)) {
                onRefresh?.(model);
            }
        }, listenerID);

        return () => {
            model.onChange.removeListener(listenerID);
            SessionHandler.onRecentDataUpdated.removeListener(listenerID);
        };
    }, [model, onRefresh]);

    return {
        refreshDevice,
    };
};
